import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";

import type { CognitoUserExtended } from "~/v2/api/auth";
import { initAmplify } from "~/v2/api/auth/config";

type AuthStatus = "loading" | "ready";

interface AuthContextProps {
  authStatus: AuthStatus;
  cognitoUser: CognitoUserExtended | null;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextProps>({
  authStatus: "loading",
  cognitoUser: null,
  isAuthenticated: false,
});

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>("loading");
  const [cognitoUser, setCognitoUser] = useState<CognitoUserExtended | null>(
    null,
  );
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    initAmplify();

    Auth.currentAuthenticatedUser()
      .then((user) => {
        Sentry.setUser({ email: user?.attributes?.email });
        setCognitoUser(user);
        setAuthStatus("ready");
      })
      .catch(() => {
        setAuthStatus("ready");
      });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authStatus,
        cognitoUser,
        isAuthenticated: authStatus === "ready" && cognitoUser !== null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
